// src/pages/ConsentTemplates/index.js
//
// Klinik Şablonları › Onam Formları (Faz 5). Düz liste (design-principles §3):
// kliniğin kendi şablonları + sistem varsayılanları. Satır tıklanınca sağdan
// drawer: kendi şablonu düzenlenir, sistem şablonu salt-okunur — "Kopyala ve
// düzenle" ile kliniğe kopyalanır. Araç satırında kliniğin onam ayarı:
// onamsız seans uyarıyla mı kaydedilsin, engellensin mi.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Drawer, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { AssignmentTurnedIn as ConsentIcon, DeleteOutline as DeleteIcon } from "@mui/icons-material";
import useAuth from "../../hooks/useAuth";
import { useAlert } from "../../context/AlertProvider";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useClinicTheme } from "../../theme";
import { DataTableCard } from "../../components/organisms";
import { ActionButton, ChoiceChipGroup, DrawerFooter, DrawerHeader, FormSelect, FormTextField } from "../../components/atoms";
import DrawerField from "../../components/molecules/DrawerField";
import { tileInputProps } from "../../components/molecules/InfoTile";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import {
  ClinicRecordListLayout,
  ClickableRow,
  DateCell,
  EDGE_L,
  EDGE_R,
  FillerRow,
  PlainHead,
  ROWS_PER_PAGE_OPTIONS,
  SkeletonRows,
  SortHead,
  StatusPill,
  TextCell,
  cellSx,
  useClientList,
} from "../../components/organisms/ClinicRecordList";
import { COLORS, TYPO } from "../../styles/uiConstants";
import {
  useListConsentTemplatesMutation,
  useCreateConsentTemplateMutation,
  useUpdateConsentTemplateMutation,
  useDeleteConsentTemplateMutation,
  useListTraditionalPracticesMutation,
  useGetConsentSettingsMutation,
  useUpdateConsentSettingsMutation,
} from "../../store/traditionalApi";
import useTerms from "../../hooks/useTerms";

const COLS = 5;
const SEGMENTS = [
  { value: "all", label: "Tümü" },
  { value: "clinic", label: "Kliniğin" },
  { value: "system", label: "Sistem" },
];
const SETTING_OPTIONS = [
  { value: "warn", label: "Uyarıyla kaydet" },
  { value: "block", label: "Engelle" },
];
const STATUS_OPTIONS = [
  { value: "active", label: "Aktif" },
  { value: "inactive", label: "Pasif" },
];

const segmentOf = (t) => (t.isSystem ? "system" : "clinic");
const matches = (t, text) =>
  String(t.title || "").toLocaleLowerCase("tr").includes(text) ||
  String(t.practice?.name || "").toLocaleLowerCase("tr").includes(text);
const SORT_FNS = {
  title: (a, b) => String(a.title || "").localeCompare(String(b.title || ""), "tr"),
  date: (a, b) => new Date(a.updatedAt || 0) - new Date(b.updatedAt || 0),
};
const DEFAULT_DIR = { title: "asc", date: "desc" };

const emptyForm = { title: "", practiceId: "", body: "", status: "active" };
const toForm = (t) => ({
  title: t?.title || "",
  practiceId: t?.practice?._id || "",
  body: t?.body || "",
  status: t?.isActive === false ? "inactive" : "active",
});

export default function ConsentTemplates() {
  const terms = useTerms();
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { palette } = useClinicTheme();
  const { showError, showSuccess } = useAlert();
  const canManage = useHasPermission("consents:manage");

  const [listTemplates, { isLoading }] = useListConsentTemplatesMutation();
  const [createTemplate, { isLoading: creating }] = useCreateConsentTemplateMutation();
  const [updateTemplate, { isLoading: updating }] = useUpdateConsentTemplateMutation();
  const [deleteTemplate, { isLoading: deleting }] = useDeleteConsentTemplateMutation();
  const [listPractices] = useListTraditionalPracticesMutation();
  const [getSettings] = useGetConsentSettingsMutation();
  const [updateSettings, { isLoading: savingSetting }] = useUpdateConsentSettingsMutation();

  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [practices, setPractices] = useState([]);
  const [requireConsent, setRequireConsent] = useState(false);
  // drawer: { mode: "create"|"edit"|"view", template }
  const [drawer, setDrawer] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const res = await listTemplates({ token }).unwrap();
      setRows(res?.data?.items || []);
    } catch (_) {
      showError("Onam şablonları yüklenemedi.");
    } finally {
      setLoaded(true);
    }
  }, [token, listTemplates, showError]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!token) return;
    listPractices({ token, status: "active", limit: 200 })
      .unwrap()
      .then((res) => setPractices(res?.data?.items || []))
      .catch(() => setPractices([]));
    getSettings({ token })
      .unwrap()
      .then((res) => setRequireConsent(Boolean(res?.data?.settings?.requireConsentForSessions)))
      .catch(() => {});
  }, [token, listPractices, getSettings]);

  const list = useClientList(rows, {
    segmentOf,
    matches,
    sorters: SORT_FNS,
    defaultDir: DEFAULT_DIR,
    initialSort: "title",
  });
  const segments = useMemo(
    () => SEGMENTS.map((s) => ({ value: s.value, label: `${s.label} · ${list.counts[s.value] || 0}` })),
    [list.counts]
  );

  const practiceOptions = useMemo(
    () => [{ value: "", label: "Genel (tüm uygulamalar)" }, ...practices.map((p) => ({ value: p._id, label: p.name }))],
    [practices]
  );

  const openRow = (t) => {
    setForm(toForm(t));
    setDrawer({ mode: t.isSystem || !canManage ? "view" : "edit", template: t });
  };
  const openCreate = (prefill) => {
    setForm(prefill ? { ...toForm(prefill), title: `${prefill.title} (kopya)`, status: "active" } : emptyForm);
    setDrawer({ mode: "create", template: null });
  };
  const closeDrawer = () => setDrawer(null);

  const handleSettingChange = async (value) => {
    const next = value === "block";
    const prev = requireConsent;
    setRequireConsent(next);
    try {
      await updateSettings({ token, requireConsentForSessions: next }).unwrap();
      showSuccess(next ? "Onamsız seans artık kaydedilemez." : "Onamsız seans uyarıyla kaydedilir.");
    } catch (e) {
      setRequireConsent(prev);
      showError(e?.data?.message || "Ayar kaydedilemedi.");
    }
  };

  const canSubmit = Boolean(form.title.trim() && form.body.trim());
  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (drawer?.mode === "view" && drawer.template) {
      openCreate(drawer.template);
      return;
    }
    const payload = {
      token,
      title: form.title.trim(),
      body: form.body.trim(),
      practiceId: form.practiceId || "",
      isActive: form.status === "active",
    };
    try {
      if (drawer?.mode === "create") {
        await createTemplate(payload).unwrap();
        showSuccess("Onam şablonu oluşturuldu.");
      } else {
        await updateTemplate({ ...payload, templateId: drawer.template._id }).unwrap();
        showSuccess("Onam şablonu güncellendi.");
      }
      closeDrawer();
      refresh();
    } catch (e) {
      showError(e?.data?.message || "Şablon kaydedilemedi.");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteTemplate({ token, templateId: drawer.template._id }).unwrap();
      showSuccess("Onam şablonu silindi.");
      setConfirmDelete(false);
      closeDrawer();
      refresh();
    } catch (e) {
      showError(e?.data?.message || "Şablon silinemedi.");
    }
  };

  const busy = isLoading && !loaded;
  const empty = list.searchText
    ? { title: "Şablon bulunamadı", message: `“${list.searchText}” aramanızla eşleşen onam şablonu yok.` }
    : { title: "Bu filtrede şablon yok", message: "Yeni bir onam şablonu oluşturabilir ya da sistem şablonunu kopyalayabilirsiniz." };
  const sortProps = { sort: list.sort, dir: list.dir, onSort: list.onSort };
  const readOnly = drawer?.mode === "view";

  return (
    <>
      <ClinicRecordListLayout
        icon={<ConsentIcon sx={{ fontSize: 22 }} />}
        title="Onam Formları"
        subtitle="Geleneksel uygulamalar için onam metinleri ve onamsız seans ayarı"
        search={list.search}
        onSearchChange={list.setSearch}
        searchPlaceholder="Şablon adı veya uygulama ile ara"
        filters={
          <Box sx={{ ml: { sm: "auto" }, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>Onamsız seans:</Typography>
            <ChoiceChipGroup
              options={SETTING_OPTIONS}
              value={requireConsent ? "block" : "warn"}
              onChange={handleSettingChange}
              disabled={!canManage || savingSetting}
              ariaLabel="Onamsız seans ayarı"
            />
            {canManage ? (
              <ActionButton variant="add" compact palette={palette} label="Yeni Şablon" onClick={() => openCreate(null)} />
            ) : null}
          </Box>
        }
        segments={segments}
        segment={list.segment}
        onSegmentChange={list.setSegment}
        segmentAriaLabel="Şablon kaynağı"
      >
        <DataTableCard
      loading={Boolean(busy)}
          variant="flat"
          empty={!busy && list.filtered.length === 0}
          emptyIcon={<ConsentIcon />}
          emptyTitle={empty.title}
          emptyMessage={empty.message}
          page={list.page}
          rowsPerPage={list.rowsPerPage}
          totalCount={list.filtered.length}
          onPageChange={list.onPageChange}
          onRowsPerPageChange={list.onRowsPerPageChange}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        >
          <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <SortHead field="title" label="Şablon" {...sortProps} sx={{ ...EDGE_L, width: "38%" }} />
                <PlainHead sx={{ width: "20%" }}>Uygulama</PlainHead>
                <PlainHead sx={{ width: "14%" }}>Kaynak</PlainHead>
                <SortHead field="date" label="Güncelleme" defaultDir="desc" {...sortProps} sx={{ width: "14%" }} />
                <PlainHead sx={{ ...EDGE_R, width: "14%" }}>Durum</PlainHead>
              </TableRow>
            </TableHead>
            <TableBody>
              {busy ? (
                <SkeletonRows rows={6} cols={COLS} />
              ) : list.pagedRows.length ? (
                list.pagedRows.map((t) => (
                  <ClickableRow key={t._id} onClick={() => openRow(t)} palette={palette}>
                    <TextCell
                      main={t.title}
                      sub={String(t.body || "").slice(0, 90) + (String(t.body || "").length > 90 ? "…" : "")}
                      sx={EDGE_L}
                    />
                    <TextCell main={t.practice?.name || "Genel"} />
                    <TextCell main={t.isSystem ? "Sistem" : "Kliniğin"} />
                    <DateCell value={t.updatedAt} />
                    <TableCell sx={{ ...cellSx, ...EDGE_R }}>
                      <StatusPill
                        label={t.isActive === false ? "Pasif" : "Aktif"}
                        tone={t.isActive === false ? "neutral" : "success"}
                        palette={palette}
                      />
                    </TableCell>
                  </ClickableRow>
                ))
              ) : (
                <FillerRow cols={COLS} />
              )}
            </TableBody>
          </Table>
        </DataTableCard>
      </ClinicRecordListLayout>

      <Drawer
        anchor="right"
        open={Boolean(drawer)}
        onClose={closeDrawer}
        PaperProps={{ sx: { width: { xs: "100%", sm: 640 }, display: "flex", flexDirection: "column", bgcolor: COLORS.white } }}
      >
        <DrawerHeader
          icon={<ConsentIcon />}
          title={drawer?.mode === "create" ? "Yeni Onam Şablonu" : readOnly ? "Onam Şablonu" : "Onam Şablonunu Düzenle"}
          subtitle={
            readOnly
              ? drawer?.template?.isSystem
                ? "Sistem şablonu — düzenlemek için kliniğinize kopyalayın"
                : "Yalnızca görüntüleme"
              : "Paragrafları boş satırla ayırın"
          }
          onClose={closeDrawer}
        />
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 3, py: 2.5 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, rowGap: 2.5, columnGap: 2 }}>
            <DrawerField label="Başlık *" fullWidth>
              <FormTextField
                palette={palette}
                variant="standard"
                size="small"
                fullWidth
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                disabled={readOnly}
                inputProps={{ maxLength: 200, "aria-label": "Başlık" }}
                InputProps={tileInputProps(!readOnly)}
              />
            </DrawerField>
            <DrawerField label="Uygulama türü">
              <FormSelect
                value={form.practiceId}
                onChange={(v) => setForm((f) => ({ ...f, practiceId: v }))}
                options={practiceOptions}
                placeholder="Genel (tüm uygulamalar)"
                variant="standard"
                size="small"
                fullWidth
                disabled={readOnly}
                InputProps={tileInputProps(!readOnly)}
              />
            </DrawerField>
            <DrawerField label="Durum">
              <ChoiceChipGroup
                options={STATUS_OPTIONS}
                value={form.status}
                onChange={(v) => setForm((f) => ({ ...f, status: v }))}
                disabled={readOnly}
                palette={palette}
                ariaLabel="Şablon durumu"
                sx={{ mt: 0.5 }}
              />
            </DrawerField>
            <DrawerField
              label="Onam metni *"
              fullWidth
              hint={`${terms.patient} onamı kaydedilirken bu metin o anki haliyle kopyalanır; sonradan yapılan değişiklik eski kayıtları etkilemez.`}
            >
              <FormTextField
                palette={palette}
                variant="standard"
                size="small"
                fullWidth
                multiline
                minRows={14}
                value={form.body}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                disabled={readOnly}
                inputProps={{ maxLength: 20000, "aria-label": "Onam metni" }}
                InputProps={tileInputProps(!readOnly)}
              />
            </DrawerField>
          </Box>
          {drawer?.mode === "edit" ? (
            <Button
              startIcon={<DeleteIcon sx={{ fontSize: 18 }} />}
              onClick={() => setConfirmDelete(true)}
              sx={{ mt: 2, textTransform: "none", fontWeight: 600, fontSize: TYPO.label, color: COLORS.errorDark, px: 1.5 }}
            >
              Şablonu sil
            </Button>
          ) : null}
        </Box>
        <DrawerFooter
          onCancel={closeDrawer}
          cancelLabel={readOnly ? "Kapat" : "Vazgeç"}
          onSubmit={readOnly && !(canManage && drawer?.template?.isSystem) ? undefined : handleSubmit}
          submitLabel={readOnly ? "Kopyala ve düzenle" : "Kaydet"}
          loadingLabel="Kaydediliyor..."
          loading={creating || updating}
          disabled={!canSubmit}
          fullWidth
        />
      </Drawer>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Onam şablonu silinsin mi?"
        message="Şablon listeden kaldırılır. Bu şablonla alınmış onam kayıtları etkilenmez."
        confirmText="Sil"
        cancelText="Vazgeç"
        confirmVariant="error"
        loading={deleting}
      />
    </>
  );
}
